import React, { useState, useEffect } from 'react';
import { Cog6ToothIcon, ArrowPathIcon, CheckCircleIcon, ExclamationCircleIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { apiClient } from '../../utils/simpleApiClient';
import SuperAdminLayout from '../../components/admin/SuperAdminLayout';

interface Setting {
  id: number;
  setting_key: string;
  name: string;
  description: string | null;
  category: string;
  category_name: string;
  data_type: string;
  value: string | null;
  parsed_value: any;
  default_value: string | null;
  options: any[] | null;
  validation_rules: string[] | null;
  metadata: any;
  is_system: boolean;
  is_public: boolean;
  sort_order: number;
  updated_by: string | null;
  updated_at: string;
}

interface Category {
  key: string;
  name: string;
  icon: string;
}

const GeneralSettingsPage: React.FC = () => {
  const [settings, setSettings] = useState<Setting[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);
  const [editedValues, setEditedValues] = useState<Record<string, any>>({});
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    loadCategories();
    loadSettings();
  }, [selectedCategory]);

  const loadCategories = async () => {
    try {
      // apiClient.get already returns parsed JSON data
      const data = await apiClient.get('/api/admin/general-settings/categories') as { categories: Category[] };
      setCategories(data.categories);
    } catch (error) {
      console.error('Error loading categories:', error);
      toast.error('Failed to load setting categories');
    }
  };

  const loadSettings = async () => {
    try {
      setLoading(true);
      const url = selectedCategory === 'all'
        ? '/api/admin/general-settings'
        : `/api/admin/general-settings?category=${selectedCategory}`;
      // apiClient.get already returns parsed JSON data
      const data = await apiClient.get(url) as { settings: Setting[] };
      setSettings(data.settings);
    } catch (error) {
      console.error('Error loading settings:', error);
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleValueChange = (key: string, value: any) => {
    setEditedValues(prev => ({
      ...prev,
      [key]: value
    }));
    setHasChanges(true);
  };

  const handleSaveSetting = async (key: string) => {
    if (!(key in editedValues)) return;

    setSaving(key);
    try {
      // apiClient.put already returns parsed JSON data
      const data = await apiClient.put(`/api/admin/general-settings/${key}`, {
        value: editedValues[key]
      }) as { success: boolean, message: string, setting: Setting };
      
      if (data.success) {
        toast.success(data.message);
        // Update the setting in the list
        setSettings(prev => prev.map(s => s.setting_key === key ? data.setting : s));
        // Remove from edited values
        setEditedValues(prev => {
          const newValues = { ...prev };
          delete newValues[key];
          return newValues;
        });
        // Check if there are still changes
        setHasChanges(Object.keys(editedValues).length > 1);
      }
    } catch (error) {
      console.error('Error saving setting:', error);
      toast.error('Failed to save setting');
    } finally {
      setSaving(null);
    }
  };

  const handleSaveAll = async () => {
    if (!hasChanges) return;

    setSaving('all');
    try {
      // apiClient.post already returns parsed JSON data
      const data = await apiClient.post('/api/admin/general-settings/batch', {
        settings: editedValues
      }) as { success: boolean, message: string };
      
      if (data.success) {
        toast.success(data.message);
        setEditedValues({});
        setHasChanges(false);
        await loadSettings();
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setSaving(null);
    }
  };

  const handleResetSetting = async (key: string) => {
    if (!confirm('Are you sure you want to reset this setting to its default value?')) return;

    setSaving(key);
    try {
      // apiClient.post already returns parsed JSON data
      const data = await apiClient.post(`/api/admin/general-settings/${key}/reset`) as { success: boolean, message: string, setting: Setting };
      
      if (data.success) {
        toast.success(data.message);
        setSettings(prev => prev.map(s => s.setting_key === key ? data.setting : s));
        // Remove from edited values if it was being edited
        if (key in editedValues) {
          setEditedValues(prev => {
            const newValues = { ...prev };
            delete newValues[key];
            return newValues;
          });
          setHasChanges(Object.keys(editedValues).length > 1);
        }
      }
    } catch (error) {
      console.error('Error resetting setting:', error);
      toast.error('Failed to reset setting');
    } finally {
      setSaving(null);
    }
  };

  const renderSettingInput = (setting: Setting) => {
    const currentValue = setting.setting_key in editedValues 
      ? editedValues[setting.setting_key] 
      : (setting.value ?? setting.default_value ?? '');

    switch (setting.data_type) {
      case 'textarea':
        const rows = setting.metadata?.rows || 5;
        const maxLength = setting.metadata?.maxLength || 5000;
        return (
          <div className="w-full">
            <textarea
              value={currentValue}
              onChange={(e) => handleValueChange(setting.setting_key, e.target.value)}
              className="w-full px-4 py-3 bg-[#1e293b] border border-[#374151] text-white rounded-lg focus:outline-none focus:border-[#6366f1] focus:ring-2 focus:ring-[#6366f1]/20 transition-all duration-200 placeholder-[#9ca3af] font-mono text-sm"
              rows={rows}
              maxLength={maxLength}
              placeholder={`Enter ${setting.name}...`}
            />
            <div className="mt-2 flex justify-between text-xs text-[#94a3b8]">
              <span>{currentValue.length} / {maxLength} characters</span>
              {setting.validation_rules && (
                <span className="text-[#60a5fa]">Required field</span>
              )}
            </div>
          </div>
        );

      case 'text':
        return (
          <input
            type="text"
            value={currentValue}
            onChange={(e) => handleValueChange(setting.setting_key, e.target.value)}
            className="w-full px-4 py-2.5 bg-[#1e293b] border border-[#374151] text-white rounded-lg focus:outline-none focus:border-[#6366f1] focus:ring-2 focus:ring-[#6366f1]/20 transition-all duration-200 placeholder-[#9ca3af]"
            placeholder={`Enter ${setting.name}...`}
          />
        );

      case 'number':
        return (
          <input
            type="number"
            value={currentValue}
            onChange={(e) => handleValueChange(setting.setting_key, e.target.value)}
            className="w-full px-4 py-2.5 bg-[#1e293b] border border-[#374151] text-white rounded-lg focus:outline-none focus:border-[#6366f1] focus:ring-2 focus:ring-[#6366f1]/20 transition-all duration-200 placeholder-[#9ca3af]"
            placeholder={`Enter ${setting.name}...`}
            min={setting.metadata?.min}
            max={setting.metadata?.max}
            step={setting.metadata?.step}
          />
        );

      case 'boolean':
      case 'checkbox':
        return (
          <label className="flex items-center cursor-pointer">
            <div className="relative">
              <input
                type="checkbox"
                checked={currentValue === '1' || currentValue === true}
                onChange={(e) => handleValueChange(setting.setting_key, e.target.checked ? '1' : '0')}
                className="sr-only"
              />
              <div className={`w-11 h-6 rounded-full transition-colors duration-200 ${
                currentValue === '1' || currentValue === true ? 'bg-[#6366f1]' : 'bg-[#374151]'
              }`}>
                <div className={`absolute left-0.5 top-0.5 w-5 h-5 bg-white rounded-full transition-transform duration-200 ${
                  currentValue === '1' || currentValue === true ? 'translate-x-5' : ''
                }`}></div>
              </div>
            </div>
            <span className="ml-3 text-sm text-[#cbd5e1]">
              {currentValue === '1' || currentValue === true ? 'Enabled' : 'Disabled'}
            </span>
          </label>
        );

      case 'select':
        return (
          <select
            value={currentValue}
            onChange={(e) => handleValueChange(setting.setting_key, e.target.value)}
            className="w-full px-4 py-2.5 bg-[#1e293b] border border-[#374151] text-white rounded-lg focus:outline-none focus:border-[#6366f1] focus:ring-2 focus:ring-[#6366f1]/20 transition-all duration-200"
          >
            <option value="">Select an option...</option>
            {setting.options?.map((option, idx) => (
              <option key={idx} value={option.value || option}>
                {option.label || option}
              </option>
            ))}
          </select>
        );

      default:
        return (
          <input
            type="text"
            value={currentValue}
            onChange={(e) => handleValueChange(setting.setting_key, e.target.value)}
            className="w-full px-4 py-2.5 bg-[#1e293b] border border-[#374151] text-white rounded-lg focus:outline-none focus:border-[#6366f1] focus:ring-2 focus:ring-[#6366f1]/20 transition-all duration-200 placeholder-[#9ca3af]"
            placeholder={`Enter ${setting.name}...`}
          />
        );
    }
  };

  if (loading) {
    return (
      <SuperAdminLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#6366f1]"></div>
        </div>
      </SuperAdminLayout>
    );
  }

  return (
    <SuperAdminLayout>
      <div className="container mx-auto px-4 max-w-7xl">
        {/* Header Section */}
        <div className="mb-8">
          <div className="flex justify-between items-start mb-2">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <Cog6ToothIcon className="h-8 w-8 text-[#6366f1]" />
                <h1 className="text-3xl font-bold text-white">General Settings</h1>
              </div>
              <p className="text-[#94a3b8] text-sm">Configure system-wide settings and preferences</p>
            </div>
            {hasChanges && (
              <button
                onClick={handleSaveAll}
                disabled={saving === 'all'}
                className="flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-[#6366f1] to-[#8b5cf6] text-white rounded-lg hover:shadow-lg hover:shadow-[#6366f1]/25 transition-all duration-200 transform hover:-translate-y-0.5 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving === 'all' ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Saving...</span>
                  </>
                ) : (
                  <>
                    <CheckCircleIcon className="h-5 w-5" />
                    <span>Save All Changes</span>
                  </>
                )}
              </button>
            )}
          </div>
        </div>

        {/* Category Filter */}
        <div className="mb-6">
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={() => setSelectedCategory('all')}
              className={`px-4 py-2 rounded-lg font-medium transition-all duration-200 ${
                selectedCategory === 'all'
                  ? 'bg-[#6366f1] text-white shadow-lg shadow-[#6366f1]/25'
                  : 'bg-[#1e293b] text-[#94a3b8] hover:bg-[#334155] hover:text-white border border-[#374151]'
              }`}
            >
              All Settings
            </button>
            {categories.map(category => (
              <button
                key={category.key}
                onClick={() => setSelectedCategory(category.key)}
                className={`px-4 py-2 rounded-lg font-medium transition-all duration-200 flex items-center gap-2 ${
                  selectedCategory === category.key
                    ? 'bg-[#6366f1] text-white shadow-lg shadow-[#6366f1]/25'
                    : 'bg-[#1e293b] text-[#94a3b8] hover:bg-[#334155] hover:text-white border border-[#374151]'
                }`}
              >
                <span>{category.icon}</span>
                <span>{category.name}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Settings List */}
        <div className="space-y-4">
          {settings.length === 0 ? (
            <div className="bg-[#1e293b] border border-[#374151] rounded-xl p-8 text-center">
              <ExclamationCircleIcon className="h-12 w-12 text-[#94a3b8] mx-auto mb-3" />
              <p className="text-[#94a3b8]">No settings found in this category</p>
            </div>
          ) : (
            settings.map(setting => (
              <div
                key={setting.setting_key}
                className="bg-[#1e293b] border border-[#374151] rounded-xl p-6 hover:border-[#4b5563] transition-all duration-200"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-semibold text-white">{setting.name}</h3>
                      {setting.is_system && (
                        <span className="px-2 py-1 text-xs font-medium bg-[#6366f1]/20 text-[#818cf8] rounded-full border border-[#6366f1]/30">
                          System
                        </span>
                      )}
                      {setting.setting_key in editedValues && (
                        <span className="px-2 py-1 text-xs font-medium bg-[#f59e0b]/20 text-[#fbbf24] rounded-full border border-[#f59e0b]/30">
                          Modified
                        </span>
                      )}
                    </div>
                    {setting.description && (
                      <p className="text-sm text-[#94a3b8] mb-4">{setting.description}</p>
                    )}
                  </div>
                </div>

                {/* Setting Input */}
                <div className="mt-4">
                  {renderSettingInput(setting)}
                </div>

                {/* Metadata */}
                <div className="mt-4 flex items-center gap-4 text-xs text-[#64748b]">
                  <span>Key: <code className="text-[#60a5fa] bg-[#0f172a] px-2 py-1 rounded">{setting.setting_key}</code></span>
                  <span>Type: <span className="text-[#94a3b8]">{setting.data_type}</span></span>
                  {setting.updated_by && (
                    <span>Last updated by: <span className="text-[#94a3b8]">{setting.updated_by}</span></span>
                  )}
                  {setting.updated_at && (
                    <span>Updated: <span className="text-[#94a3b8]">{new Date(setting.updated_at).toLocaleDateString()}</span></span>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </SuperAdminLayout>
  );
};

export default GeneralSettingsPage;